import { Modal } from 'antd'
import dayjs from 'dayjs'
import i18n from 'i18next'
import resso from 'resso'
import { cdnApi } from '@/api'
import * as Api from '@/api/account'
import * as homeApi from '@/api/home'
import { getUserInfo } from './api/account'
import { eventTracking, showPlanModal } from './libs/util'
import { CreateVideoStore } from './views/create-video/create-video-state'

export interface IUserPackage {
  membership_credits: number
  current_membership_credits: number
  current_membership_package_title: string
  membership_expire_time: number
  membership_expired: boolean
  permanent_credits: number
  free_credits: number
  current_membership_level: number
  invitor_aff_code?: string
  duration_type: number
  hifly_id?: string
}

export interface IProduct {
  title: string
  duration_type: number
  id: number
  unit_credits: number
  units: number
  total_price: number
  discounted_unit_price: number
  original_unit_price: number
  level: number
  status: number
  tag: string
  activity: string
  sale_opening_time?: number
  details?: string[]
  url?: string
}

export const UserStore = resso({
  phone: '',
  uid: '',
  device_id: localStorage.getItem('di') || '',
  userPackage: undefined as unknown as IUserPackage,
  systemInfo: undefined as any,
  showQuestion: false,
  showQuestionModal: false,
  activityModalOpen: false,
  versionNumber: +(localStorage.getItem('version-20250409') || 1),
  studyCenterClick: !!localStorage.getItem('studyCenter'),
  creationList: [],
  creditLog: undefined as any,
  productOrder: undefined as any,
  pointOrder: undefined as any,
  payStatus: 0,
  planModalInstance: undefined as any,
  loginModalInstance: undefined as any,
  loginStatus: 0,
  declareChecked: false,
  systemGuideStep: undefined as any,
  systemReady: false,
  sensitiveWords: [] as any[],
  sensitiveWordsPinyin: [] as any[],
  sensitiveWhiteList: [] as string[],
  couponTip: 1,
  couponModalInstance: undefined as any,
  setUserToken: (d: any) => {
    localStorage.setItem('token', d.token)
    getUserInfo()
  },
  setUserInfo: (d: any) => {
    UserStore.phone = d.phone
    UserStore.uid = d.uid
  },
  getToken: () => {
    return localStorage.getItem('token')
  },
  loginOut: () => {
    eventTracking('SystemLogout')
    UserStore.phone = ''
    UserStore.uid = ''
    UserStore.userPackage = undefined as any
    localStorage.removeItem('token')
    CreateVideoStore.highSetting = {
      drive_mode: 0,
      only_generate_audio: false,
      mode: -1, // 0-普通模式 1-暗黑模式
      model_version: 0 // 0-1.0 1-1.5
    }
  },
  // 检查套餐是否过期或者即将过期
  checkUserPackage: async () => {
    const res: IUserPackage = await Api.getUserPackage()
    UserStore.userPackage = res || {}

    if (res?.membership_expire_time) {
      const diff = dayjs(res.membership_expire_time * 1000).diff(dayjs(Date.now()), 'second') / 3600
      const vipToExpried = !res.membership_expired && diff > 0 && diff <= 24
      const vipExpried = res.membership_expired
      const expriedTime = dayjs(res.membership_expire_time * 1000).format('YYYY-MM-DD HH:mm')

      if (!sessionStorage.getItem('vipToExpried') && vipToExpried) {
        sessionStorage.setItem('vipToExpried', 'true')
        return Modal.confirm({
          title: i18n.t('global_states.vip_expire_soon_title'),
          okText: i18n.t('global_states.renew_upgrade'),
          onOk: () => {
            showPlanModal()
          },
          content: i18n.t('global_states.vip_expire_soon_content', { time: expriedTime })
        })
      } else if (!sessionStorage.getItem('vipExpried') && vipExpried) {
        sessionStorage.setItem('vipExpried', 'true')
        return Modal.confirm({
          title: i18n.t('global_states.vip_expired_title'),
          okText: i18n.t('global_states.renew_upgrade'),
          onOk: () => {
            showPlanModal()
          },
          content: i18n.t('global_states.vip_expired_content', { time: expriedTime })
        })
      }
    }

    return false
  },
  updateUserPackage: async () => {
    const res: IUserPackage = await Api.getUserPackage()
    UserStore.userPackage = res || {}
    return res
  },
  updatePayStatus: () => {
    UserStore.payStatus = UserStore.payStatus + 1
    UserStore.updateUserPackage()
  },
  getQaCompleted: async () => {
    const [question, product] = await Promise.all([homeApi.getQuestionCompleted(), Api.getProductList()])
    UserStore.showQuestion = !question?.completed
    if (!product?.list?.find((p: any) => p.activity?.includes('activity'))) {
      UserStore.showQuestionModal = !question?.completed
    }
  },
  getClaimedCoupons: async () => {
    const res = await homeApi.getClaimedCoupons()
    const first_membership_coupon = res?.list?.find((l: any) => l.type === 1 && l.status === 2)
    return first_membership_coupon
  },
  getCreditLogs: async (page?: number, size?: number) => {
    const res = await Api.getAllCreditLogs({
      page: page || 0,
      size: size || 8
    })
    UserStore.creditLog = res || {}
  },
  getProductOrders: async (page?: number, size?: number) => {
    const res = await Api.getMembershipOrders({
      page: page || 0,
      size: size || 8
    })
    UserStore.productOrder = res || {}
  },
  getPointOrders: async (page?: number, size?: number) => {
    const res = await Api.getPointOrders({
      page: page || 0,
      size: size || 8
    })
    UserStore.pointOrder = res || {}
  },
  initSensitiveWords: async () => {
    const [content, pinyinContent, sensitiveWhiteList] = await Promise.all([
      cdnApi.get(`hiflys/sensitive_words_lines.json?date=${Date.now()}`),
      cdnApi.get(`hiflys/sensitive_words_pinyin.json?date=${Date.now()}`),
      homeApi.getSensitiveWhiteList()
    ])
    UserStore.sensitiveWhiteList = sensitiveWhiteList?.list || []
    UserStore.sensitiveWords = content || []
    UserStore.sensitiveWordsPinyin = pinyinContent || []
  }
})

export const InteractStore = resso({
  selectAvatar: undefined as any,
  selectTts: undefined as any,
  selectKnowLedge: undefined as any,
  roomId: undefined as any,
  interactPendingNum: 0,
  interactStart: false,
  duration_seconds: 120,
  initDigital: async () => {
    const res = await homeApi.getDigitalList()
    const defaultAvatar = (res.list || []).filter((l: any) => l.is_public)?.[0]
    if (defaultAvatar) {
      const digitalAvatar = await homeApi.getDigitalAvatar(defaultAvatar.id)
      InteractStore.selectAvatar = {
        ...(digitalAvatar.list || [])[0],
        group_id: defaultAvatar.id,
        group_title: defaultAvatar.name,
        is_public: true
      }
    } else {
      InteractStore.selectAvatar = {}
    }
  },
  initVoice: async () => {
    const res = await homeApi.getGlobalTtsGroups({ for_avachat: true })
    const defaultVoice = res?.list?.[0]
    if (defaultVoice) {
      const detail = await homeApi.getVoicGroupDetail(defaultVoice.id)
      InteractStore.selectTts = {
        ...(detail.list || [])[0],
        group_id: defaultVoice.id,
        group_title: defaultVoice.title,
        is_public: true
      }
    } else {
      InteractStore.selectTts = {}
    }
  },
  initKnowledge: async () => {
    const res = await homeApi.getAgentList()
    const defaultAgent = (res.list || []).filter((l: any) => l.is_public)?.[0]
    if (defaultAgent) {
      InteractStore.selectKnowLedge = {
        ...defaultAgent,
        is_public: true
      }
    } else {
      InteractStore.selectKnowLedge = {}
    }
  },
  updateRoom: (roomId: number) => {
    InteractStore.roomId = roomId
  }
})

export const WSStore = resso({
  interactWS: undefined as unknown as WebSocket,
  setInteractWS: (ws: WebSocket) => {
    WSStore.interactWS = ws
  },
  cleanInteractWS: () => {
    if (WSStore.interactWS) {
      WSStore.interactWS?.close()
      WSStore.interactWS = undefined as unknown as WebSocket
    }
  }
})
